import { useState, useEffect } from 'react';
import { Save, Plus, X, RefreshCw, Mail, MapPin, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import adminApi from '@/lib/adminApi';

const SettingsManager = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const fetchSettings = async () => {
    try {
      const response = await adminApi.get(`/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const addEmail = () => {
    if (!newEmail.trim()) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast.error('Email invalide');
      return;
    }
    if (settings.contact_emails.includes(newEmail.trim())) {
      toast.error('Cet email existe déjà');
      return;
    }
    setSettings(prev => ({
      ...prev,
      contact_emails: [...prev.contact_emails, newEmail.trim()]
    }));
    setNewEmail('');
  };

  const removeEmail = (email) => {
    if (settings.contact_emails.length <= 1) {
      toast.error('Vous devez garder au moins un email destinataire');
      return;
    }
    setSettings(prev => ({
      ...prev,
      contact_emails: prev.contact_emails.filter(e => e !== email)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/settings`, {
        contact_emails: settings.contact_emails,
        site_title: settings.site_title,
        meta_description: settings.meta_description,
        confirmation_message: settings.confirmation_message,
        address: settings.address,
        phone: settings.phone,
        email: settings.email
      });
      toast.success('Paramètres enregistrés');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1e3a8a]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-playfair text-2xl font-semibold text-[#0F172A]">
            Paramètres du site
          </h2>
          <p className="text-slate-600 font-inter text-sm mt-1">
            Configuration générale et informations de contact
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#1e3a8a] hover:bg-[#172554]"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Enregistrer
        </Button>
      </div>

      {/* Contact Emails */}
      <Card>
        <CardHeader>
          <CardTitle className="font-playfair text-lg flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#1e3a8a]" />
            Emails destinataires du formulaire
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600 font-inter">
            Les messages envoyés via le formulaire de contact seront reçus à ces adresses.
          </p>
          
          <div className="flex flex-wrap gap-2">
            {settings?.contact_emails?.map((email, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded"
              >
                <span className="text-sm font-inter">{email}</span>
                <button
                  onClick={() => removeEmail(email)}
                  className="text-slate-400 hover:text-red-500 transition-colors"
                  data-testid={`remove-email-${idx}`}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <Input
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Ajouter un email..."
              className="flex-1"
              onKeyPress={(e) => e.key === 'Enter' && addEmail()}
              data-testid="new-email-input"
            />
            <Button onClick={addEmail} variant="outline" data-testid="add-email-btn">
              <Plus size={18} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="font-playfair text-lg flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#1e3a8a]" />
            Coordonnées affichées sur le site
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="font-inter text-sm">Adresse</Label>
            <Input
              value={settings?.address || ''}
              onChange={(e) => handleChange('address', e.target.value)}
              className="mt-2"
              placeholder="2 avenue de Marbotte, 21000 Dijon"
              data-testid="address-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="font-inter text-sm">Téléphone</Label>
              <Input
                value={settings?.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="mt-2"
                placeholder="03 80 65 91 00"
                data-testid="phone-input"
              />
            </div>
            <div>
              <Label className="font-inter text-sm">Email de contact public</Label>
              <Input
                value={settings?.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                className="mt-2"
                placeholder="contact@placefinanciere-bfc.fr"
                data-testid="public-email-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SEO & Metadata */}
      <Card>
        <CardHeader>
          <CardTitle className="font-playfair text-lg">SEO & Métadonnées</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="font-inter text-sm">Titre du site</Label>
            <Input
              value={settings?.site_title || ''}
              onChange={(e) => handleChange('site_title', e.target.value)}
              className="mt-2"
              placeholder="Place Financière Bourgogne-Franche-Comté"
              data-testid="site-title-input"
            />
          </div>

          <div>
            <Label className="font-inter text-sm">Meta description</Label>
            <Textarea
              value={settings?.meta_description || ''}
              onChange={(e) => handleChange('meta_description', e.target.value)}
              className="mt-2"
              placeholder="Description pour les moteurs de recherche..."
              data-testid="meta-description-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Recommandé : 150-160 caractères
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Message */}
      <Card>
        <CardHeader>
          <CardTitle className="font-playfair text-lg">Message de confirmation</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label className="font-inter text-sm">
              Message affiché après envoi du formulaire de contact
            </Label>
            <Textarea
              value={settings?.confirmation_message || ''}
              onChange={(e) => handleChange('confirmation_message', e.target.value)}
              className="mt-2"
              placeholder="Votre message a bien été envoyé..."
              data-testid="confirmation-message-input"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsManager;
